import { useEffect, useMemo } from 'react';
import { App, Form, Input, Modal, Select } from 'antd';
import { useEnterprises } from '@/hooks/useEnterprises';
import { useProfiles } from '@/hooks/useProfiles';
import { useCreateDevice, useUpdateDevice } from '@/hooks/useDevices';
import type { DeviceDto } from '@/types/device';

interface Props {
  open: boolean;
  device: DeviceDto | null;
  onClose: () => void;
}

interface FormValues {
  enterpriseId: number;
  profileId?: number;
  serialNumber?: string;
  imeiNumber?: string;
  brand?: string;
  model?: string;
  assignedToName?: string;
  assignedToEmail?: string;
  assignedToPhone?: string;
  expectedPin?: string;
}

export function DeviceFormModal({ open, device, onClose }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const isEdit = device != null;

  const { data: enterprises } = useEnterprises({ pageSize: 100 });
  const enterpriseId = Form.useWatch('enterpriseId', form);

  // Cargar perfiles solo de la enterprise seleccionada
  const { data: profiles } = useProfiles({
    enterpriseId,
    pageSize: 100,
  });

  const createMut = useCreateDevice();
  const updateMut = useUpdateDevice();

  useEffect(() => {
    if (open) {
      if (device) {
        form.setFieldsValue({
          enterpriseId: device.enterpriseId,
          profileId: device.profileId ?? undefined,
          serialNumber: device.serialNumber ?? '',
          imeiNumber: device.imeiNumber ?? '',
          brand: device.brand ?? '',
          model: device.model ?? '',
          assignedToName: device.assignedToName ?? '',
          assignedToEmail: device.assignedToEmail ?? '',
          assignedToPhone: device.assignedToPhone ?? '',
          expectedPin: device.expectedPin,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({ expectedPin: '0321' });
      }
    }
  }, [open, device, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      if (isEdit && device) {
        await updateMut.mutateAsync({
          id: device.id,
          request: {
            profileId: values.profileId,
            assignedToName: values.assignedToName?.trim() || undefined,
            assignedToEmail: values.assignedToEmail?.trim() || undefined,
            assignedToPhone: values.assignedToPhone?.trim() || undefined,
            expectedPin: values.expectedPin?.trim() || undefined,
          },
        });
        message.success('Dispositivo actualizado.');
      } else {
        await createMut.mutateAsync({
          enterpriseId: values.enterpriseId,
          profileId: values.profileId,
          serialNumber: values.serialNumber?.trim() || undefined,
          imeiNumber: values.imeiNumber?.trim() || undefined,
          brand: values.brand?.trim() || undefined,
          model: values.model?.trim() || undefined,
          assignedToName: values.assignedToName?.trim() || undefined,
          assignedToEmail: values.assignedToEmail?.trim() || undefined,
          assignedToPhone: values.assignedToPhone?.trim() || undefined,
          expectedPin: values.expectedPin?.trim() || '0321',
        });
        message.success('Dispositivo creado.');
      }

      onClose();
    } catch (err: any) {
      if (err?.errorFields) return;
    }
  };

  return (
    <Modal
      title={isEdit ? `Editar dispositivo: ${device?.assignedToName ?? `#${device?.id}`}` : 'Nuevo dispositivo'}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText={isEdit ? 'Guardar cambios' : 'Crear'}
      cancelText="Cancelar"
      confirmLoading={createMut.isPending || updateMut.isPending}
      destroyOnClose
      width={640}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          label="Enterprise"
          name="enterpriseId"
          rules={[{ required: true, message: 'Enterprise requerida.' }]}
        >
          <Select
            placeholder="Selecciona una Enterprise"
            options={(enterprises?.items ?? []).map((e) => ({ value: e.id, label: e.displayNameEs }))}
            disabled={isEdit}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>

        <Form.Item label="Perfil" name="profileId" extra="El perfil define las apps y politicas. Opcional al crear.">
          <Select
            placeholder="Selecciona un perfil (opcional)"
            options={(profiles?.items ?? []).map((p) => ({ value: p.id, label: p.displayNameEs }))}
            allowClear
            disabled={!enterpriseId}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item label="Marca" name="brand">
            <Input placeholder="Samsung, Motorola..." maxLength={100} />
          </Form.Item>

          <Form.Item label="Modelo" name="model">
            <Input placeholder="Galaxy A10s, Moto G..." maxLength={100} />
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item label="Numero de serie" name="serialNumber">
            <Input maxLength={100} disabled={isEdit} />
          </Form.Item>

          <Form.Item label="IMEI" name="imeiNumber">
            <Input maxLength={50} disabled={isEdit} />
          </Form.Item>
        </div>

        <Form.Item label="Asignado a" name="assignedToName">
          <Input placeholder="Nombre del responsable" maxLength={200} />
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item
            label="Email"
            name="assignedToEmail"
            rules={[{ type: 'email', message: 'Email invalido.' }]}
          >
            <Input placeholder="responsable@empresa.com" />
          </Form.Item>

          <Form.Item label="Telefono" name="assignedToPhone">
            <Input placeholder="+54 11 1234-5678" maxLength={50} />
          </Form.Item>
        </div>

        <Form.Item
          label="PIN esperado"
          name="expectedPin"
          extra="PIN/passcode que debe tener configurado el dispositivo (default 0321)."
        >
          <Input maxLength={50} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
